import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../utils/api";

const sameOwner = (ownerId, currentUserId) =>
  ownerId != null &&
  currentUserId != null &&
  String(ownerId) === String(currentUserId);

export function useArrowSystem({
  id,
  userId,
  hasJoined,
  gameState,
  config,
  isRoomStarted,
  attackPercent,
  // Arrow drawing state managed by orchestrator
  drawingArrowType,
  setDrawingArrowType,
  currentArrowPath,
  setCurrentArrowPath,
  // Mode helpers from orchestrator
  enterDrawMode,
  setUiMode,
  uiMode,
  setError,
}) {
  const [activeAttackArrows, setActiveAttackArrows] = useState([]); // [{path, remainingPower, status, ...}]
  const [activeDefendArrow, setActiveDefendArrow] = useState(null); // {path: [{x,y}...], remainingPower, ...}
  const prevArrowsRef = useRef({ attacks: [], defend: null });

  const handleStartDrawArrow = (type) => {
    if (!isRoomStarted) return;
    // Clear any existing drawing state
    setCurrentArrowPath([]);
    enterDrawMode(type);
  };

  const handleCancelArrow = () => {
    setDrawingArrowType(null);
    setCurrentArrowPath([]);
    if (uiMode === "drawAttack" || uiMode === "drawDefend") {
      setUiMode("idle");
    }
  };

  const handleArrowPathUpdate = (path) => {
    setCurrentArrowPath(path);
  };

  const handleSendArrow = async (type, path) => {
    if (!userId || !hasJoined) return;
    if (!isRoomStarted) return;
    if (!path || path.length < 2) return;

    const playerNation = gameState?.gameState?.nations?.find((n) =>
      sameOwner(n.owner, userId)
    );
    const population = playerNation?.population || 0;
    const initialPower = population * attackPercent;

    try {
      const response = await apiFetch(`api/gamerooms/${id}/arrow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          path,
          percent: attackPercent,
        }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to send arrow command");
      }

      const result = await response.json();

      if (type === "attack") {
        setActiveAttackArrows((prev) => [
          ...prev,
          {
            id: result.arrowId,
            path,
            percent: attackPercent,
            troopCommitment: attackPercent,
            remainingPower: 0,
            initialPower: 0,
            effectiveDensityAtFront: 0,
            currentIndex: 1,
            status: "advancing",
            frontWidth: 0,
            opposingForces: [],
            headX: path[0].x,
            headY: path[0].y,
          },
        ]);
      } else if (type === "defend") {
        setActiveDefendArrow({
          path,
          percent: attackPercent,
          troopCommitment: attackPercent,
          remainingPower: 0,
          effectiveDensityAtFront: 0,
          currentIndex: 0,
        });
      }

      setDrawingArrowType(null);
      setCurrentArrowPath([]);
      setUiMode("idle");
    } catch (err) {
      // Clear drawing state so the red line goes away
      setDrawingArrowType(null);
      setCurrentArrowPath([]);
      setUiMode("idle");
      setError?.(err.message || "Arrow command failed");
      console.warn("[ARROW] Error:", err.message);
    }
  };

  const handleClearActiveArrow = async (type, arrowId) => {
    if (!userId || !hasJoined) return;

    try {
      await apiFetch(`api/gamerooms/${id}/clearArrow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          arrowId,
        }),
      });

      if (type === "attack") {
        if (arrowId) {
          setActiveAttackArrows((prev) => prev.filter((a) => a.id !== arrowId));
        } else {
          setActiveAttackArrows([]);
        }
      } else if (type === "defend") {
        setActiveDefendArrow(null);
      }
    } catch (err) {
      console.error("Failed to clear arrow:", err);
    }
  };

  const handleReinforceArrow = async (arrowId, percent = 0.1) => {
    if (!userId || !hasJoined) return;
    try {
      await apiFetch(`api/gamerooms/${id}/reinforceArrow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          arrowId,
          percent,
        }),
      });
    } catch (err) {
      console.error("Failed to reinforce arrow:", err);
    }
  };

  const handleRetreatArrow = async (arrowId) => {
    if (!userId || !hasJoined) return;
    try {
      await apiFetch(`api/gamerooms/${id}/retreatArrow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          arrowId,
        }),
      });
    } catch (err) {
      console.error("Failed to retreat arrow:", err);
    }
  };

  // Track arrow state from server and detect completions
  useEffect(() => {
    const playerNation = gameState?.gameState?.nations?.find(
      (n) => sameOwner(n.owner, userId) && n.status !== "defeated"
    );
    const serverArrows = playerNation?.arrowOrders || {};
    const tickCount = Number(gameState?.tickCount);
    const tickRateMs = config?.territorial?.tickRateMs || 100;
    const minArrowDurationMs = config?.territorial?.minArrowDurationMs ?? 10000;
    const maxArrowDurationMs = config?.territorial?.maxArrowDurationMs ?? 120000;
    const arrowDurationPerPowerMs = config?.territorial?.arrowDurationPerPowerMs ?? 25;
    const arrowMaxStallTicks = config?.territorial?.arrowMaxStallTicks ?? 6;

    const isValidArrowPath = (path) =>
      Array.isArray(path) &&
      path.length >= 2 &&
      path.every(
        (point) =>
          point &&
          Number.isFinite(point.x) &&
          Number.isFinite(point.y)
      );

    const isStaleArrow = (arrow) => {
      if (!arrow) return true;
      if (!isValidArrowPath(arrow.path)) return true;

      // Density mode: arrows use troopCommitment instead of remainingPower
      const isDensityArrow = arrow.troopCommitment != null && arrow.troopCommitment > 0;

      if (!isDensityArrow) {
        // Legacy power-budget mode
        const remaining = Number(arrow.remainingPower ?? 0);
        if (!Number.isFinite(remaining) || remaining <= 0) return true;
        if (arrow.status === "retreating" && remaining <= 1) return true;
      }

      if (
        Number.isFinite(arrow.currentIndex) &&
        arrow.currentIndex >= arrow.path.length
      ) {
        return true;
      }
      if (Number.isFinite(arrow.stalledTicks) && Number.isFinite(arrowMaxStallTicks)) {
        if (isDensityArrow) {
          const densityStallLimit = Math.max(20, arrowMaxStallTicks * 4);
          if (arrow.stalledTicks >= densityStallLimit) return true;
        } else {
          if (arrow.stalledTicks >= arrowMaxStallTicks) return true;
        }
      }
      // Density arrows with no troops at front get removed quickly
      if (isDensityArrow && Number.isFinite(arrow.emptyFrontTicks) && arrow.emptyFrontTicks >= 10) {
        return true;
      }

      if (!isDensityArrow) {
        const remaining = Number(arrow.remainingPower ?? 0);
        let initialPower = Number(arrow.initialPower ?? remaining ?? 0);
        if (!Number.isFinite(initialPower)) initialPower = 0;
        const scaledDuration =
          minArrowDurationMs + initialPower * arrowDurationPerPowerMs;
        const maxDurationMs = Math.max(
          minArrowDurationMs,
          Math.min(maxArrowDurationMs, scaledDuration)
        );
        if (Number.isFinite(arrow.createdAtTick) && Number.isFinite(tickCount)) {
          const ageMs = (tickCount - arrow.createdAtTick) * tickRateMs;
          if (ageMs > maxDurationMs + tickRateMs * 2) return true;
        } else if (arrow.createdAt) {
          const rawAge =
            Date.now() - new Date(arrow.createdAt).getTime();
          if (Number.isFinite(rawAge) && rawAge > maxDurationMs + 2000) {
            return true;
          }
        }
      }
      return false;
    };

    // Handle legacy single attack -> attacks[] migration on client
    let serverAttacks = serverArrows.attacks || [];
    if (serverArrows.attack && !serverArrows.attacks) {
      serverAttacks = [serverArrows.attack];
    }

    // Always sync client arrows to match server state, with stale cleanup
    const filteredAttacks = Array.isArray(serverAttacks)
      ? serverAttacks.filter((arrow) => !isStaleArrow(arrow))
      : [];
    setActiveAttackArrows(filteredAttacks);

    if (serverArrows.defend && isValidArrowPath(serverArrows.defend.path)) {
      const defendArrow = serverArrows.defend;
      setActiveDefendArrow(isStaleArrow(defendArrow) ? null : defendArrow);
    } else if (!serverArrows.defend) {
      setActiveDefendArrow(null);
    }

    prevArrowsRef.current = {
      attacks: serverAttacks,
      defend: serverArrows.defend || null,
    };
  }, [gameState, userId, config]);

  return {
    activeAttackArrows,
    activeDefendArrow,
    handleStartDrawArrow,
    handleCancelArrow,
    handleArrowPathUpdate,
    handleSendArrow,
    handleClearActiveArrow,
    handleReinforceArrow,
    handleRetreatArrow,
  };
}
